/**
 * SUPER SEARCH v3 – LeJan Brand
 * Recall-first intent-aware search.
 *
 * Philosophy: fetch BROADLY from Shopify, then filter + rank client-side.
 * Shopify predictive search is limited — it only finds products whose indexed
 * fields contain the exact query. Our products are named "Lejan One® - Black",
 * not "zapatilla negra", so we must:
 *   1. Always include a "catch-all" query (brand name) to guarantee recall
 *   2. Send the most useful individual keywords for targeted results
 *   3. Do ALL the smart filtering client-side using tags, metafields, title, type
 */
(function () {
  'use strict';

  /* ══════════════════════════════════════════════
     1. CONFIGURATION
  ══════════════════════════════════════════════ */
  var BRAND_QUERY    = 'Lejan';      // always fetched as fallback for recall
  var KIDS_QUERY     = 'Lejan Kids'; // fetched when gender=kids detected
  var MAX_FETCHES    = 5;
  var DEBOUNCE_MS    = 300;
  var MAX_RESULTS    = 20;
  var DISPLAY_LIMIT  = 4;            // show only 4 in the drawer (Lejan custom style)

  /* ══════════════════════════════════════════════
     2. FACETED DICTIONARY
     Every known term is mapped to:
       - facet type (color, category, gender, material, attribute, style)
       - canonical values: the normalized forms we'll match against product data
  ══════════════════════════════════════════════ */
  var FACETS = {
    color: {
      // Spanish
      'negro':['black'],'negra':['black'],'negros':['black'],'negras':['black'],
      'blanco':['white'],'blanca':['white'],'blancos':['white'],'blancas':['white'],
      'rojo':['red'],'roja':['red'],'azul':['blue','navy'],'azules':['blue','navy'],
      'verde':['green','olive','khaki'],'amarillo':['yellow'],'amarilla':['yellow'],
      'naranja':['orange'],'rosa':['pink'],'morado':['purple'],'morada':['purple'],
      'violeta':['violet','purple'],'gris':['grey','gray'],
      'marron':['brown'],'marrón':['brown'],'beige':['beige','cream','sand'],
      'crema':['cream','beige'],'dorado':['gold','golden'],'dorada':['gold','golden'],
      'plateado':['silver'],'plateada':['silver'],
      'burdeos':['burgundy','wine'],'granate':['burgundy','maroon','garnet'],
      'coral':['coral'],'turquesa':['turquoise','teal'],
      'lila':['lilac','lavender'],'celeste':['light blue','sky blue'],
      'caqui':['khaki'],'kaki':['khaki'],'camel':['camel','tan'],
      'arena':['sand'],'leopardo':['leopard'],
      'multicolor':['multicolor','multi'],
      'chocolate':['chocolate','brown'],'mostaza':['mustard'],
      // English
      'black':['negro'],'white':['blanco'],'red':['rojo'],'blue':['azul'],
      'navy':['azul','marino'],'green':['verde'],'yellow':['amarillo'],
      'orange':['naranja'],'pink':['rosa'],'purple':['morado','violeta'],
      'grey':['gris'],'gray':['gris'],'brown':['marron'],'cream':['crema','beige'],
      'gold':['dorado'],'silver':['plateado'],'burgundy':['burdeos','granate'],
      'khaki':['caqui','kaki'],'sand':['arena'],'leopard':['leopardo'],
      'mustard':['mostaza'],'sky blue':['celeste']
    },
    category: {
      // Spanish → English
      'zapatilla':['sneaker','shoe','trainer'],'zapatillas':['sneakers','shoes','trainers'],
      'zapato':['shoe','footwear'],'zapatos':['shoes','footwear'],
      'calzado':['footwear','shoe','shoes'],
      'sandalia':['sandal'],'sandalias':['sandals'],
      'bota':['boot'],'botas':['boots'],
      'chancla':['flip flop','slide'],'chanclas':['flip flops','slides'],
      'calcetin':['sock'],'calcetín':['sock'],'calcetines':['socks'],
      'limpiador':['cleaner','cleaning'],'kit':['kit','set'],
      // English → Spanish
      'sneaker':['zapatilla'],'sneakers':['zapatillas'],
      'shoe':['zapato','zapatilla'],'shoes':['zapatos','zapatillas'],
      'boot':['bota'],'boots':['botas'],
      'sandal':['sandalia'],'sandals':['sandalias'],
      'sock':['calcetin'],'socks':['calcetines']
    },
    gender: {
      // Spanish
      'mujer':['women','woman','female','adulto','adult'],
      'mujeres':['women','female','adulto','adult'],
      'hombre':['men','man','male','adulto','adult'],
      'hombres':['men','male','adulto','adult'],
      'unisex':['unisex'],
      'niño':['kid','kids','boy','child'],'nino':['kid','kids','boy','child'],
      'niña':['kid','kids','girl','child'],'nina':['kid','kids','girl','child'],
      'niños':['kids','children'],'ninos':['kids','children'],
      'adulto':['adult','adults','men','women','unisex'],
      'adultos':['adults','adult','men','women','unisex'],
      'adulta':['adult','adults','women','unisex'],
      // English
      'women':['mujer','adulto'],'woman':['mujer','adulto'],
      'men':['hombre','adulto'],'man':['hombre','adulto'],
      'kids':['niños','ninos'],'kid':['niño','nino'],
      'children':['niños','ninos'],
      'boy':['niño','nino','kid'],'girl':['niña','nina','kid']
    },
    material: {
      'cuero':['leather'],'piel':['leather','skin'],
      'tela':['fabric','textile','canvas'],'lona':['canvas'],
      'ante':['suede'],'napa':['napa','leather'],
      'algodón':['cotton'],'algodon':['cotton'],
      'reciclado':['recycled'],'reciclada':['recycled'],
      'mesh':['mesh','malla'],'malla':['mesh'],
      'leather':['cuero','piel'],'suede':['ante'],
      'canvas':['lona','tela'],'cotton':['algodon'],
      'recycled':['reciclado']
    },
    attribute: {
      'cordones':['laces','lace-up'],'velcro':['velcro'],
      'slip-on':['slip-on','slip on'],
      'elástico':['elastic'],'elastico':['elastic'],
      'sostenible':['sustainable','eco'],
      'vegano':['vegan'],'vegana':['vegan'],
      'respetuoso':['respectful','barefoot'],'respetuosa':['respectful','barefoot'],
      'minimalista':['minimalist','barefoot'],
      'barefoot':['barefoot','respetuoso','minimalista'],
      'flexible':['flexible'],
      'ligero':['lightweight','light'],'ligera':['lightweight','light'],
      'cómodo':['comfortable','comfort'],'comodo':['comfortable','comfort'],
      'cómoda':['comfortable','comfort'],'comoda':['comfortable','comfort'],
      'suela fina':['thin sole','barefoot'],
      'drop cero':['zero drop','barefoot'],
      'zero drop':['zero drop','drop cero'],
      'puntera ancha':['wide toe box','wide toe'],
      'wide':['ancho','ancha'],'ancho':['wide'],'ancha':['wide']
    },
    style: {
      'bajo':['low','low-top'],'baja':['low','low-top'],
      'bajos':['low','low-top'],'bajas':['low','low-top'],
      'alto':['high','high-top','boot'],'alta':['high','high-top','boot'],
      'altos':['high','high-top'],'altas':['high','high-top'],
      'deportivo':['sport','athletic'],'deportiva':['sport','athletic'],
      'casual':['casual'],'clasico':['classic'],'clasica':['classic'],
      'clásico':['classic'],'clásica':['classic'],
      'retro':['retro','vintage'],'vintage':['vintage','retro'],
      'urbano':['urban'],'urbana':['urban'],
      'elegante':['elegant','dressy'],
      'low':['bajo'],'high':['alto'],'sport':['deportivo'],
      'classic':['clasico'],'urban':['urbano']
    }
  };

  /* Flat lookup: normalized word → [{ facet, original, translations }] */
  var WORD_LOOKUP = {};
  Object.keys(FACETS).forEach(function (facetName) {
    var entries = FACETS[facetName];
    Object.keys(entries).forEach(function (term) {
      var key = norm(term);
      if (!WORD_LOOKUP[key]) WORD_LOOKUP[key] = [];
      WORD_LOOKUP[key].push({
        facet: facetName,
        original: term,
        translations: entries[term]
      });
    });
  });

  /* Noise words: ignored for facet classification */
  var NOISE = new Set([
    'de','del','la','el','los','las','un','una','unos','unas',
    'con','sin','para','por','en','y','o','e','a',
    'color','colour','tipo','talla','size','modelo',
    'the','of','for','and','or','with','in'
  ]);

  /* ══════════════════════════════════════════════
     3. HELPERS
  ══════════════════════════════════════════════ */
  function norm(str) {
    return (str || '').toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s\-]/g, '').trim();
  }
  function escapeHtml(str) {
    var d = document.createElement('div');
    d.appendChild(document.createTextNode(str || ''));
    return d.innerHTML;
  }
  function debounce(fn, delay) {
    var t;
    return function () {
      var c = this, a = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(c, a); }, delay);
    };
  }
  function formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    return (cents / 100).toFixed(2).replace('.', ',') + ' \u20AC';
  }
  function setHas(set, val) {
    var found = false;
    set.forEach(function(v) { if (v === val) found = true; });
    return found;
  }
  function setToArray(set) {
    var arr = [];
    set.forEach(function(v) { arr.push(v); });
    return arr;
  }

  /* ══════════════════════════════════════════════
     4. INTENT PARSER
  ══════════════════════════════════════════════ */
  function parseIntent(query) {
    var normalized = norm(query);
    var words = normalized.split(/\s+/).filter(function (w) { return w.length > 0; });
    var intent = {};           // facetName → Set of canonical terms
    var classifiedWords = {};  // word → true
    var unclassified = [];

    // Multi-word phrases first
    var remaining = normalized;
    Object.keys(FACETS).forEach(function (fn) {
      Object.keys(FACETS[fn]).forEach(function (phrase) {
        var np = norm(phrase);
        if (np.indexOf(' ') === -1) return;
        if (remaining.indexOf(np) !== -1) {
          if (!intent[fn]) intent[fn] = new Set();
          intent[fn].add(np);
          FACETS[fn][phrase].forEach(function (t) { intent[fn].add(norm(t)); });
          np.split(/\s+/).forEach(function (w) { classifiedWords[w] = true; });
          remaining = remaining.replace(np, ' ').trim();
        }
      });
    });

    // Single words
    words.forEach(function (w) {
      if (classifiedWords[w]) return;
      if (NOISE.has(w)) return;
      var lookups = WORD_LOOKUP[w];
      if (lookups && lookups.length > 0) {
        lookups.forEach(function (entry) {
          if (!intent[entry.facet]) intent[entry.facet] = new Set();
          intent[entry.facet].add(w);
          intent[entry.facet].add(norm(entry.original));
          entry.translations.forEach(function (t) { intent[entry.facet].add(norm(t)); });
        });
        classifiedWords[w] = true;
      } else {
        unclassified.push(w);
      }
    });

    // Detect if query is purely generic shoe terms (no specific product name)
    var hasCategory = !!intent.category;
    var hasGender   = !!intent.gender;
    var hasColor    = !!intent.color;
    var isGenericShoeQuery = hasCategory || hasGender || hasColor;

    return {
      facets: intent,
      unclassified: unclassified,
      allWords: words,
      isGenericShoeQuery: isGenericShoeQuery,
      hasKids: hasGender && (
        setHas(intent.gender, 'kid') || setHas(intent.gender, 'kids') ||
        setHas(intent.gender, 'nino') || setHas(intent.gender, 'nina') ||
        setHas(intent.gender, 'ninos') || setHas(intent.gender, 'children') ||
        setHas(intent.gender, 'boy') || setHas(intent.gender, 'girl') ||
        setHas(intent.gender, 'child')
      ),
      hasAdult: hasGender && (
        setHas(intent.gender, 'adulto') || setHas(intent.gender, 'adult') ||
        setHas(intent.gender, 'adults') || setHas(intent.gender, 'mujer') ||
        setHas(intent.gender, 'hombre') || setHas(intent.gender, 'women') ||
        setHas(intent.gender, 'men') || setHas(intent.gender, 'man') ||
        setHas(intent.gender, 'woman')
      )
    };
  }

  /* ══════════════════════════════════════════════
     5. SMART QUERY BUILDER
     Key insight: always include the brand name "Lejan" as a query
     because that's what actually matches your products in Shopify.
     Then add targeted queries for colors/names that ARE in your titles.
  ══════════════════════════════════════════════ */
  /* Capitalize first letter of each word — Shopify Predictive Search
     can be case-sensitive on some indexed fields, so "Wide" finds more
     results than "wide" when the product title is "Lejan Wide®". */
  function capitalizeWords(str) {
    return str.replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); });
  }

  function buildSmartQueries(query, intent) {
    var queries = [];
    var added = {};
    function addQ(q) {
      var k = q.toLowerCase().trim();
      if (k && !added[k] && queries.length < MAX_FETCHES) {
        added[k] = true;
        // Always capitalize to maximize Shopify Predictive Search recall
        queries.push(capitalizeWords(q.trim()));
      }
    }

    // 1. Original user query (capitalized for best recall)
    addQ(query);

    // 2. ALWAYS fetch brand name for broad recall of actual products
    if (intent.isGenericShoeQuery) {
      if (intent.hasKids) {
        addQ(KIDS_QUERY);
      } else {
        addQ(BRAND_QUERY);
      }
    }

    // 3. Colors → these ARE in your product titles (e.g. "- Black", "- Kaki")
    if (intent.facets.color) {
      intent.facets.color.forEach(function (t) {
        if (t.length >= 3) addQ(t);
      });
    }

    // 4. Unclassified words (could be product names like "One", "Wide", "Napa")
    intent.unclassified.forEach(function (w) {
      if (w.length >= 2) addQ(w);
    });

    // 5. ALL original user words as individual queries — they might be product
    //    names even if classified as facets (e.g. "wide" = attribute AND product name)
    intent.allWords.forEach(function (w) {
      if (!NOISE.has(w) && w.length >= 2) addQ(w);
    });

    // 6. Key translations for recall
    Object.keys(intent.facets).forEach(function (fn) {
      if (fn === 'color') return; // already handled
      var firstTerm = null;
      intent.facets[fn].forEach(function(t) { if (!firstTerm && t.length >= 3) firstTerm = t; });
      if (firstTerm) addQ(firstTerm);
    });

    return queries;
  }

  /* ══════════════════════════════════════════════
     6. PRODUCT SCORING
     Score based on how well a product matches the detected intent.
     Key rules:
     - If only category detected (e.g. "zapatilla") → show all shoes, exclude kits/socks
     - If only gender detected (e.g. "niño") → filter by Kids in title/tags
     - If color detected → product must have that color
     - Multi-facet: soft AND — reward products matching more facets
     - Never hard-exclude: just rank lower
  ══════════════════════════════════════════════ */
  function isShoeProduct(p) {
    // Returns true if product is a shoe/sneaker (not a kit, cleaner, sock, etc.)
    var t = norm(p.title + ' ' + p.type + ' ' + p.tags);
    if (t.indexOf('cleaner') !== -1 || t.indexOf('cleaning') !== -1) return false;
    if (t.indexOf('limpiador') !== -1) return false;
    if (norm(p.type) === 'kit' || norm(p.type) === 'set') return false;
    if (t.indexOf('sock') !== -1 || t.indexOf('calcetin') !== -1) return false;
    return true;
  }

  function isKidsProduct(p) {
    var t = norm(p.title + ' ' + p.tags + ' ' + p.metaGender + ' ' + p.metaCategory);
    return t.indexOf('kids') !== -1 || t.indexOf('kid') !== -1 ||
           t.indexOf('nino') !== -1 || t.indexOf('nina') !== -1 ||
           t.indexOf('children') !== -1 || t.indexOf('child') !== -1;
  }

  function isAdultProduct(p) {
    return !isKidsProduct(p);
  }

  function textContainsAny(text, terms) {
    var n = norm(text);
    var found = false;
    terms.forEach(function (t) {
      if (!found && n.indexOf(norm(t)) !== -1) found = true;
    });
    return found;
  }

  function scoreProduct(product, intent) {
    var score = 0;
    var facetNames = Object.keys(intent.facets);
    var facetCount = facetNames.length;
    var facetsMatched = 0;

    var title     = norm(product.title + ' ' + product.subtitle);
    var type      = norm(product.type);
    var vendor    = norm(product.vendor);
    var tags      = norm(product.tags);
    var color     = norm(product.metaColor);
    var material  = norm(product.metaMaterial);
    var closure   = norm(product.metaClosure);
    var category  = norm(product.metaCategory);
    var features  = norm(product.metaFeatures);
    var gender    = norm(product.metaGender);
    var style     = norm(product.metaStyle);
    var allText   = [title, type, vendor, tags, color, material, closure,
                     category, features, gender, style].join(' ');

    // --- Score each detected facet ---
    facetNames.forEach(function (facetName) {
      var terms = setToArray(intent.facets[facetName]);
      var matched = false;

      for (var i = 0; i < terms.length && !matched; i++) {
        var term = terms[i];
        switch (facetName) {
          case 'color':
            if (color && color.indexOf(term) !== -1) { score += 80; matched = true; }
            else if (title.indexOf(term) !== -1) { score += 70; matched = true; }
            else if (tags.indexOf(term) !== -1) { score += 50; matched = true; }
            break;

          case 'category':
            // For category: if it's a shoe term, check if product IS a shoe
            var isShoe = isShoeProduct(product);
            var isShoeTerm = ['zapatilla','zapatillas','zapato','zapatos','calzado',
                              'sneaker','sneakers','shoe','shoes','trainer','trainers','footwear'].indexOf(term) !== -1;
            if (isShoeTerm && isShoe) { score += 50; matched = true; }
            else if (category.indexOf(term) !== -1) { score += 50; matched = true; }
            else if (type.indexOf(term) !== -1) { score += 50; matched = true; }
            else if (title.indexOf(term) !== -1) { score += 40; matched = true; }
            else if (tags.indexOf(term) !== -1) { score += 35; matched = true; }
            break;

          case 'gender':
            // Special: "niño/kids" → match Kids products; "mujer/hombre" → match Adult products
            if (intent.hasKids) {
              if (isKidsProduct(product)) { score += 60; matched = true; }
            } else if (intent.hasAdult) {
              if (isAdultProduct(product)) { score += 60; matched = true; }
            }
            if (!matched && gender && gender.indexOf(term) !== -1) { score += 50; matched = true; }
            if (!matched && tags.indexOf(term) !== -1) { score += 40; matched = true; }
            if (!matched && allText.indexOf(term) !== -1) { score += 25; matched = true; }
            break;

          case 'material':
            if (material && material.indexOf(term) !== -1) { score += 60; matched = true; }
            else if (title.indexOf(term) !== -1) { score += 50; matched = true; }
            else if (tags.indexOf(term) !== -1) { score += 40; matched = true; }
            break;

          case 'attribute':
            if (features.indexOf(term) !== -1) { score += 55; matched = true; }
            else if (style.indexOf(term) !== -1) { score += 55; matched = true; }
            else if (closure.indexOf(term) !== -1) { score += 55; matched = true; }
            else if (tags.indexOf(term) !== -1) { score += 40; matched = true; }
            else if (allText.indexOf(term) !== -1) { score += 30; matched = true; }
            break;

          case 'style':
            if (style && style.indexOf(term) !== -1) { score += 55; matched = true; }
            else if (tags.indexOf(term) !== -1) { score += 40; matched = true; }
            else if (title.indexOf(term) !== -1) { score += 35; matched = true; }
            else if (allText.indexOf(term) !== -1) { score += 20; matched = true; }
            break;
        }
      }

      if (matched) facetsMatched++;
    });

    // --- Multi-facet bonus/penalty (soft AND) ---
    if (facetCount > 1) {
      if (facetsMatched === facetCount) {
        score += 80; // matched everything
      } else if (facetsMatched >= 1) {
        // Partial match: proportional reduction but NOT elimination
        var ratio = facetsMatched / facetCount;
        score = Math.round(score * (0.3 + 0.7 * ratio));
      } else {
        // Matched nothing from facets
        score = Math.round(score * 0.1);
      }
    }

    // --- Unclassified words (product names like "one", "wide", "napa", "lejan") ---
    intent.unclassified.forEach(function (word) {
      if (title.indexOf(word) !== -1) score += 50;
      else if (vendor.indexOf(word) !== -1) score += 30;
      else if (allText.indexOf(word) !== -1) score += 20;
      // Don't penalize: unclassified words might just be noise
    });

    // --- Availability ---
    if (product.available) score += 5;
    if (!product.available) score -= 15;

    // --- Negative: suppress kits/cleaners when searching for shoes ---
    if (intent.facets.category || intent.facets.color || intent.facets.gender || intent.facets.style) {
      if (!isShoeProduct(product)) {
        score = Math.round(score * 0.15); // heavily demote non-shoes
      }
    }

    // --- Negative: wrong age group ---
    if (intent.hasKids && !isKidsProduct(product)) {
      score = Math.round(score * 0.2);
    }
    if (intent.hasAdult && isKidsProduct(product)) {
      score = Math.round(score * 0.3);
    }

    return Math.max(0, score);
  }

  /* ══════════════════════════════════════════════
     6b. BUILD SEARCH PAGE QUERY
     Translates the user's intent into terms that Shopify's native
     /search can actually match against product titles and tags.
     e.g. "zapatilla negra" → "Lejan black"
  ══════════════════════════════════════════════ */
  function buildSearchPageQuery(originalQuery, intent) {
    var parts = [];
    var added = {};
    function add(term) {
      var k = term.toLowerCase().trim();
      if (k && !added[k]) { added[k] = true; parts.push(term); }
    }

    // 1. Brand name for recall (products are named "Lejan ...")
    if (intent.isGenericShoeQuery) {
      add(intent.hasKids ? KIDS_QUERY : BRAND_QUERY);
    }

    // 2. Color translations → use the English canonical that's in the title
    //    e.g. "negra" → add "black" (titles say "Lejan One® - Black")
    if (intent.facets.color) {
      intent.facets.color.forEach(function (t) {
        // Prefer English color terms (3+ chars) that appear in product titles
        if (t.length >= 3) add(t);
      });
    }

    // 3. Unclassified words — likely product names ("One", "Wide", "Napa")
    intent.unclassified.forEach(function (w) {
      if (w.length >= 2) add(w);
    });

    // 4. Material translations if present
    if (intent.facets.material) {
      intent.facets.material.forEach(function (t) {
        if (t.length >= 3) add(t);
      });
    }

    // 5. Style translations if present
    if (intent.facets.style) {
      intent.facets.style.forEach(function (t) {
        if (t.length >= 3) add(t);
      });
    }

    // Fallback: if nothing useful was built, use the original query
    if (parts.length === 0) return originalQuery;

    return parts.join(' ');
  }

  /* ══════════════════════════════════════════════
     7. BUILD OVERLAY DOM
  ══════════════════════════════════════════════ */
  function buildOverlay() {
    var overlay = document.createElement('div');
    overlay.id = 'super-search-overlay';
    overlay.className = 'ss-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML =
      '<div class="ss-backdrop"></div>' +
      '<div class="ss-container">' +
        '<div class="ss-header">' +
          '<div class="ss-input-wrap">' +
            '<svg class="ss-icon-search" width="15" height="14" viewBox="0 0 15 14" fill="none"><circle cx="6.478" cy="5.857" r="5.143" stroke="currentColor" stroke-width="1.28571" fill="none"/><path d="M10.335 9.714l3.215 3.215" stroke="currentColor" stroke-width="1.28571" stroke-linecap="round"/></svg>' +
            '<input class="ss-input" type="search" name="q" placeholder="' + ((window.theme && window.theme.searchPlaceholder) || 'Buscar...') + '" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" />' +
            '<button class="ss-clear-btn" type="button" aria-label="Limpiar b\u00FAsqueda" style="display:none">' +
              '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
            '</button>' +
          '</div>' +
          '<button class="ss-close-btn" type="button" aria-label="Cerrar b\u00FAsqueda">' +
            '<svg width="12" height="12" viewBox="0 0 173 173" fill="currentColor"><path d="M159.53,170.69,2.31,13.47a7.89,7.89,0,0,1,0-11.16h0a7.89,7.89,0,0,1,11.16,0L170.69,159.53a7.89,7.89,0,0,1,0,11.16h0A7.89,7.89,0,0,1,159.53,170.69Z"/><path d="M13.47,170.69,170.69,13.47a7.89,7.89,0,0,0,0-11.16h0a7.89,7.89,0,0,0-11.16,0L2.31,159.53a7.89,7.89,0,0,0,0,11.16h0A7.89,7.89,0,0,0,13.47,170.69Z"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="ss-body">' +
          '<div class="ss-loading" style="display:none"><div class="ss-spinner"></div></div>' +
          '<div class="ss-results-header" style="display:none">' +
            '<span class="ss-results-count"></span>' +
            '<a class="ss-results-view-all" href="#">' + ((window.__lejanI18n && window.__lejanI18n.searchOverlay && window.__lejanI18n.searchOverlay.view_all) || 'View all') + '</a>' +
          '</div>' +
          '<div class="ss-results-products"></div>' +
          '<div class="ss-no-results" style="display:none">' +
            '<p class="ss-no-results-text">' + ((window.__lejanI18n && window.__lejanI18n.searchOverlay && window.__lejanI18n.searchOverlay.no_results) || 'No results found.') + '</p>' +
            '<p class="ss-no-results-hint">' + ((window.__lejanI18n && window.__lejanI18n.searchOverlay && window.__lejanI18n.searchOverlay.no_results_hint) || 'Try another term or browse our collections.') + '</p>' +
          '</div>' +
        '</div>' +
        '<div class="ss-footer" style="display:none">' +
          '<a class="ss-view-all" href="#">' + ((window.__lejanI18n && window.__lejanI18n.searchOverlay && window.__lejanI18n.searchOverlay.view_all_results) || 'View all results') + '</a>' +
        '</div>' +
      '</div>';
    document.body.appendChild(overlay);
    return overlay;
  }

  /* ══════════════════════════════════════════════
     8a. ADD-TO-CART HELPER (reuses theme cart drawer)
  ══════════════════════════════════════════════ */
  function ssAddToCart(variantId) {
    var sectionsToUpdate = ['page-header','cart-drawer']
      .map(function(sel){ return document.querySelector(sel); })
      .filter(function(el){ return el; });
    var sectionIds = sectionsToUpdate.map(function(el){ return el.dataset.sectionId; }).filter(Boolean);

    var fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', '1');
    if (sectionIds.length) fd.append('sections', sectionIds.join(','));
    fd.append('sections_url', window.location.pathname);

    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept':'application/javascript','X-Requested-With':'XMLHttpRequest' },
      body: fd
    })
    .then(function(res){
      if(!res.ok) return res.json().then(function(err){ throw err; });
      return res.json();
    })
    .then(function(data){
      if(data.sections && sectionsToUpdate.length > 0){
        sectionsToUpdate.forEach(function(el){
          var sid = el.dataset.sectionId;
          if(sid && data.sections[sid] && typeof el.updateFromCartChange === 'function'){
            el.updateFromCartChange(data.sections[sid]);
          }
        });
      } else {
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      }
      var drawer = document.querySelector('cart-drawer');
      if (!(drawer && drawer.hasAttribute('open'))) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: null } }));
      }
      return data;
    });
  }

  /* ══════════════════════════════════════════════
     8b. RENDER
  ══════════════════════════════════════════════ */
  function renderProducts(container, items) {
    container.innerHTML = '';
    if (!items || !items.length) return;

    var heading = document.createElement('h3');
    heading.className = 'ss-section-heading';
    heading.textContent = (window.__lejanI18n && window.__lejanI18n.searchOverlay && window.__lejanI18n.searchOverlay.products_heading) || 'Products';
    container.appendChild(heading);

    var grid = document.createElement('div');
    grid.className = 'ss-products-grid';

    items.forEach(function (item) {
      var card = document.createElement('div');
      card.className = 'ss-product-card';

      /* tarea-26: la tarjeta de la colección tal cual (product-block renderizado en el servidor).
         La barra de tallas, el «+» y el añadir los gobierna pcard-sizes.js por delegación.
         La tarjeta montada en JS de abajo queda solo como respaldo si no llegara el bloque. */
      if (item.blockHtml) {
        card.className = 'ss-product-card ss-product-card--block';
        card.innerHTML = item.blockHtml;
        grid.appendChild(card);
        return;
      }

      var safeTitle = escapeHtml(item.title);
      /* Codea v1.3: el nombre va en una línea como en product-block («Lejan Sakura - Black» →
         «Sakura Black»); los datos (title/subtitle) no cambian, que los usa el filtrado. */
      var cardName = escapeHtml([item.title.replace(/^Lejan (?=Sakura|Melrose)/, ''), item.subtitle].join(' ').trim());
      var safeSubtitle = '';

      /* ── Build ATC overlay (chips + button, INSIDE the image area) ── */
      var atcOverlay = '';
      if (item.available && item.variants && item.variants.length > 0) {
        if (item.hasOnlyDefaultVariant) {
          atcOverlay =
            '<div class="ss-atc-overlay">' +
              '<form class="ss-atc-form" data-ss-single="true">' +
                '<input type="hidden" name="id" value="' + item.variants[0].id + '">' +
                '<button type="submit" class="ss-atc-submit">' + ((window.__lejanI18n && window.__lejanI18n.searchOverlay && window.__lejanI18n.searchOverlay.add_button) || 'Add') + '</button>' +
              '</form>' +
            '</div>';
        } else {
          var chipsHtml = '';
          var seenOptions = {};
          var firstAvailableId = null;
          item.variants.forEach(function(v) {
            if (seenOptions[v.option1]) return;
            seenOptions[v.option1] = true;
            var disabledAttr = !v.available ? ' aria-disabled="true"' : '';
            var selectedClass = '';
            if (v.available && !firstAvailableId) {
              firstAvailableId = v.id;
              selectedClass = ' is-selected';
            }
            chipsHtml += '<button type="button" class="ss-atc-chip' + selectedClass + '" data-variant-id="' + v.id + '"' + disabledAttr + '>' + escapeHtml(v.option1) + '</button>';
          });
          if (!firstAvailableId && item.variants.length) firstAvailableId = item.variants[0].id;
          atcOverlay =
            '<div class="ss-atc-overlay">' +
              '<form class="ss-atc-form">' +
                '<div class="ss-atc-chips">' + chipsHtml + '</div>' +
                '<button type="submit" class="ss-atc-submit">' + ((window.__lejanI18n && window.__lejanI18n.searchOverlay && window.__lejanI18n.searchOverlay.add_button) || 'Add') + '</button>' +
                '<input type="hidden" name="id" value="' + (firstAvailableId || '') + '">' +
              '</form>' +
            '</div>';
        }
      }

      var imgContent = item.img
        ? '<div class="ss-card-img"><img src="' + escapeHtml(item.img) + '" alt="' + safeTitle + '" loading="lazy" /></div>'
        : '<div class="ss-card-img ss-card-img--placeholder"></div>';

      var priceHtml = '';
      // "Desde" solo si el producto tiene variantes a distinto precio (ej. tallas de nino mas baratas)
      var fromLabel = item.priceVaries
        ? '<span class="ss-card-price ss-card-price--from">' +
            escapeHtml((window.__lejanI18n && window.__lejanI18n.searchOverlay && window.__lejanI18n.searchOverlay.price_from) || '') +
          '</span> '
        : '';
      // Preferimos el precio formateado por Liquid (moneda y separadores correctos del market);
      // formatMoney solo actua de red de seguridad si el data-attribute no viniera.
      var showPrice   = item.priceFormatted   || formatMoney(item.price);
      var showCompare = item.compareFormatted || formatMoney(item.comparePrice);
      if (item.comparePrice && parseInt(item.comparePrice, 10) > parseInt(item.price, 10)) {
        priceHtml = fromLabel +
                    '<span class="ss-card-price ss-card-price--sale">' + escapeHtml(showPrice) + '</span>' +
                    '<span class="ss-card-price ss-card-price--compare">' + escapeHtml(showCompare) + '</span>';
      } else if (item.price) {
        priceHtml = fromLabel + '<span class="ss-card-price">' + escapeHtml(showPrice) + '</span>';
      }

      card.innerHTML =
        '<div class="ss-card-img-wrap">' +
          '<a class="ss-card-img-link" href="' + escapeHtml(item.url) + '">' + imgContent + '</a>' +
          atcOverlay +
        '</div>' +
        '<a class="ss-card-link" href="' + escapeHtml(item.url) + '">' +
          '<div class="ss-card-info">' +
            '<span class="ss-card-title">' + cardName + '</span>' +
            (safeSubtitle ? '<span class="ss-card-subtitle">' + safeSubtitle + '</span>' : '') +
            '<div class="ss-card-prices">' + priceHtml + '</div>' +
          '</div>' +
        '</a>';
      grid.appendChild(card);
    });
    container.appendChild(grid);

    /* ── Chip click → select variant ── */
    grid.addEventListener('click', function(e) {
      var chip = e.target.closest && e.target.closest('.ss-atc-chip');
      if (!chip) return;
      if (chip.getAttribute('aria-disabled') === 'true') return;
      e.preventDefault();
      var wrap = chip.closest('.ss-atc-chips');
      wrap.querySelectorAll('.ss-atc-chip').forEach(function(el) { el.classList.remove('is-selected'); });
      chip.classList.add('is-selected');
      var form = chip.closest('.ss-atc-form');
      var idInput = form.querySelector('input[name="id"]');
      if (idInput) idInput.value = chip.dataset.variantId || '';
    });

    /* ── Submit → close search, AJAX add to cart, open cart drawer ── */
    grid.addEventListener('submit', function(e) {
      var form = e.target.closest && e.target.closest('.ss-atc-form');
      if (!form) return;
      e.preventDefault();
      var btn = form.querySelector('.ss-atc-submit');
      var idInput = form.querySelector('input[name="id"]');
      if (!idInput || !idInput.value) return;
      btn.disabled = true;
      btn.textContent = '...';

      /* Close the search overlay first */
      var overlay = document.querySelector('.ss-overlay');
      if (overlay) closeSearch(overlay);

      ssAddToCart(idInput.value)
        .then(function() {})
        .catch(function(err) { console.error('SS add to cart error:', err); });
    });
  }

  /* ══════════════════════════════════════════════
     9. FETCH + PROCESS
  ══════════════════════════════════════════════ */
  var abortController = null;

  function buildFetchUrl(query) {
    var predictiveUrl = (window.theme && theme.routes && theme.routes.predictiveSearch) || '/search/suggest';
    /* Sin theme.routes.base ({{ shop.url }}, absoluta): desde theme dev (127.0.0.1)
       o cualquier preview con otro dominio era cross-origin y CORS silenciaba todos
       los resultados. Relativa al origin actual funciona en prod y en previews. */
    var url = new URL(predictiveUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', 'predictive-search-super');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,variants.sku,body');
    return url.toString();
  }

  function parseProductsFromHtml(html) {
    var products = [];
    var tpl = document.createElement('template');
    tpl.innerHTML = html;
    var root = tpl.content.querySelector('#super-search-results-data');
    if (!root) return products;
    root.querySelectorAll('.ss-product-item').forEach(function (el) {
      var titleParts = (el.dataset.title || '').split(' - ');
      var variants = [];
      try { variants = JSON.parse(el.dataset.variants || '[]'); } catch(e) {}
      products.push({
        url: el.dataset.url,
        title: titleParts[0] || '',
        subtitle: titleParts[1] || '',
        vendor: el.dataset.vendor || '',
        type: el.dataset.type || '',
        tags: el.dataset.tags || '',
        metaColor: el.dataset.metaColor || '',
        metaMaterial: el.dataset.metaMaterial || '',
        metaClosure: el.dataset.metaClosure || '',
        metaCategory: el.dataset.metaCategory || '',
        metaFeatures: el.dataset.metaFeatures || '',
        metaGender: el.dataset.metaGender || '',
        metaStyle: el.dataset.metaStyle || '',
        available: el.dataset.available === 'true',
        price: el.dataset.price,
        priceVaries: el.dataset.priceVaries === 'true',
        priceFormatted: el.dataset.priceFormatted || '',
        compareFormatted: el.dataset.compareFormatted || '',
        comparePrice: el.dataset.comparePrice,
        img: el.querySelector('.ss-product-img') ? el.querySelector('.ss-product-img').getAttribute('src') : '',
        productId: el.dataset.productId || '',
        hasOnlyDefaultVariant: el.dataset.hasOnlyDefaultVariant === 'true',
        variants: variants,
        optionName: el.dataset.optionName || '',
        blockHtml: el.querySelector('template.ss-pblock') ? el.querySelector('template.ss-pblock').innerHTML : ''
      });
    });
    return products;
  }

  function doSearch(query, overlay) {
    if (!query || query.length < 2) { clearResults(overlay); return; }

    var body = overlay.querySelector('.ss-body');
    var loading = overlay.querySelector('.ss-loading');
    var noResults = overlay.querySelector('.ss-no-results');
    var footer = overlay.querySelector('.ss-footer');
    body.classList.add('ss-body--has-results');
    loading.style.display = 'flex';
    noResults.style.display = 'none';
    footer.style.display = 'none';

    if (abortController) { abortController.abort(); abortController = null; }
    abortController = new AbortController();
    var signal = abortController.signal;

    var intent = parseIntent(query);
    var searchQueries = buildSmartQueries(query, intent);

    var fetches = searchQueries.map(function (q) {
      return fetch(buildFetchUrl(q), { method: 'GET', signal: signal })
        .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text(); })
        .then(function (html) { return parseProductsFromHtml(html); })
        .catch(function (err) { if (err.name === 'AbortError') throw err; return []; });
    });

    Promise.all(fetches)
      .then(function (results) {
        abortController = null;
        loading.style.display = 'none';
        var seen = {};
        var allProducts = [];
        results.forEach(function (prods) {
          prods.forEach(function (p) {
            // URLs carry per-query params (_pos/_psq), so dedupe by product id
            var key = p.productId || (p.url || '').split('?')[0];
            if (!seen[key]) { seen[key] = true; allProducts.push(p); }
          });
        });
        processResults(allProducts, query, intent, overlay);
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        loading.style.display = 'none';
        console.warn('Super Search error:', err);
      });
  }

  function processResults(products, query, intent, overlay) {
    // Wide-fit products share their display name with the regular fit, so
    // hide them unless the user explicitly searched for them
    if (!/\b(wide|ancho|ancha|anchos|anchas)\b/.test(norm(query))) {
      var nonWide = products.filter(function (p) {
        return !(/\bwide\b/.test(norm(p.title + ' ' + p.subtitle)) ||
                 (',' + (p.tags || '').toLowerCase() + ',').indexOf(',wide,') !== -1);
      });
      if (nonWide.length > 0) products = nonWide;
    }
    products.forEach(function (p) { p._score = scoreProduct(p, intent); });
    products.sort(function (a, b) { return b._score - a._score; });

    // Remove zero-score products only when we have enough results
    var meaningful = products.filter(function (p) { return p._score > 0; });
    if (meaningful.length > 0) {
      products = meaningful;
    }

    products = products.slice(0, MAX_RESULTS);
    var totalCount = products.length;
    var displayProducts = products.slice(0, DISPLAY_LIMIT);

    var productsContainer = overlay.querySelector('.ss-results-products');
    var noResults = overlay.querySelector('.ss-no-results');
    var footer = overlay.querySelector('.ss-footer');
    renderProducts(productsContainer, displayProducts);

    var hasResults = totalCount > 0;
    /* Relativa por lo mismo que buildFetchUrl: con shop.url el "ver todo" te sacaba
       del dev/preview al dominio de produccion. Se deriva de la ruta de sugerencias
       (/de/search/suggest -> /de/search) para conservar el prefijo de idioma de
       Markets, que shop.url tampoco llevaba. */
    var searchBase = ((window.theme && theme.routes && theme.routes.predictiveSearch) || '/search/suggest')
      .replace(/\/suggest\/?$/, '');
    var searchUrl = searchBase + '?q=' + encodeURIComponent(buildSearchPageQuery(query, intent)) + '&type=product';
    noResults.style.display = hasResults ? 'none' : 'block';

    // Results header (total count + view all)
    var resultsHeader = overlay.querySelector('.ss-results-header');
    if (hasResults) {
      footer.style.display = totalCount > DISPLAY_LIMIT ? 'flex' : 'none';
      footer.querySelector('.ss-view-all').href = searchUrl;
      resultsHeader.style.display = 'flex';
      var countTpl = (window.__lejanI18n && window.__lejanI18n.searchOverlay) ?
        (totalCount === 1 ? window.__lejanI18n.searchOverlay.results_count_one : window.__lejanI18n.searchOverlay.results_count_other) :
        '{{count}} results';
      resultsHeader.querySelector('.ss-results-count').textContent =
        (countTpl || '{{count}} results').replace('{{count}}', totalCount);
      resultsHeader.querySelector('.ss-results-view-all').href = searchUrl;
    } else {
      footer.style.display = 'none';
      resultsHeader.style.display = 'none';
    }
  }

  function clearResults(overlay) {
    overlay.querySelector('.ss-results-products').innerHTML = '';
    overlay.querySelector('.ss-no-results').style.display = 'none';
    overlay.querySelector('.ss-footer').style.display = 'none';
    overlay.querySelector('.ss-loading').style.display = 'none';
    overlay.querySelector('.ss-results-header').style.display = 'none';
    overlay.querySelector('.ss-body').classList.remove('ss-body--has-results');
  }

  /* ══════════════════════════════════════════════
     10. OPEN / CLOSE
  ══════════════════════════════════════════════ */
  function updateSearchTop() {
    var target = document.getElementById('pageheader') || document.querySelector('.section-header');
    if (!target) return;
    var bottom = Math.round(target.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--ss-search-top', bottom + 'px');
  }

  function openSearch(overlay) {
    updateSearchTop();
    overlay.setAttribute('aria-hidden', 'false');
    overlay.classList.add('ss-overlay--open');
    document.body.classList.add('show-search');
    document.body.style.overflow = 'hidden';
    setTimeout(function () { overlay.querySelector('.ss-input').focus(); }, 100);
  }
  function closeSearch(overlay) {
    overlay.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('ss-overlay--open');
    document.body.classList.remove('show-search');
    document.body.style.overflow = '';
    clearResults(overlay);
    overlay.querySelector('.ss-input').value = '';
    overlay.querySelector('.ss-clear-btn').style.display = 'none';
  }

  /* ══════════════════════════════════════════════
     11. INIT
  ══════════════════════════════════════════════ */
  function init() {
    var overlay = buildOverlay();
    var input   = overlay.querySelector('.ss-input');
    var clearBtn = overlay.querySelector('.ss-clear-btn');
    var closeBtn = overlay.querySelector('.ss-close-btn');
    var backdrop = overlay.querySelector('.ss-backdrop');

    var debouncedSearch = debounce(function () {
      var val = input.value.trim();
      clearBtn.style.display = val.length > 0 ? 'flex' : 'none';
      doSearch(val, overlay);
    }, DEBOUNCE_MS);

    input.addEventListener('input', debouncedSearch);
    input.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeSearch(overlay); });
    clearBtn.addEventListener('click', function () {
      input.value = ''; clearBtn.style.display = 'none'; clearResults(overlay); input.focus();
    });
    closeBtn.addEventListener('click', function () { closeSearch(overlay); });
    backdrop.addEventListener('click', function () { closeSearch(overlay); });

    document.addEventListener('click', function (e) {
      var sl = e.target.closest('a.show-search-link');
      if (sl) { e.preventDefault(); e.stopPropagation(); openSearch(overlay); }
    }, true);

    document.addEventListener('keydown', function (e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        overlay.classList.contains('ss-overlay--open') ? closeSearch(overlay) : openSearch(overlay);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

